import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { QuestionTypeEnum } from '../../../../enums/question-type.enum';
import { IQuestionCategory } from '../../../../interfaces/question-category.interface';

interface IQuestionTypeOption {
  label: string;
  value: QuestionTypeEnum;
}

@Component({
  selector: 'app-question-dialog',
  templateUrl: './question-dialog.component.html',
  styleUrls: ['./question-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionDialogComponent {
  showCreateCategory = false;
  form = this.fb.group({
    text: ['', Validators.required],
    type: [null, Validators.required],
    answer_choices: this.fb.array([]),
    category: [null, Validators.required],
    createCategory: [{ value: null, disabled: true }, Validators.required],
  });
  categories: IQuestionCategory[] = [];

  questionTypes = [
    { label: 'Ответ Yes/No', value: QuestionTypeEnum.Bool },
    { label: 'Текстовая строка', value: QuestionTypeEnum.String },
    { label: 'Speedtest', value: QuestionTypeEnum.Speedtest },
    {
      label: 'Множественный предвыборный ответ',
      value: QuestionTypeEnum.ChoicesMultiple,
    },
    {
      label: 'Множественный предвыборный ответ + комментарий',
      value: QuestionTypeEnum.ChoicesMultipleWithComment,
    },
    {
      label: 'Одиночный предвыборный ответ',
      value: QuestionTypeEnum.Choices,
    },
    { label: 'GPS координата', value: QuestionTypeEnum.Coordinate },
    { label: 'Фото', value: QuestionTypeEnum.Camera },
    { label: 'Фото Галерея', value: QuestionTypeEnum.Image },
    { label: 'Фото (5 шт) + комментарий', value: QuestionTypeEnum.Camera5 },
    { label: 'Фото (10 шт) + комментарий', value: QuestionTypeEnum.Camera10 },
    { label: 'Фото Галерея (5 шт) + комментарий', value: QuestionTypeEnum.Image5 },
    { label: 'Фото Галерея (10 шт) + комментарий', value: QuestionTypeEnum.Image10 },
    {
      label: 'Поле для ввода только цифровых значений',
      value: QuestionTypeEnum.Integer,
    },
    {
      label: 'Интеграция с Quality Keeper',
      value: QuestionTypeEnum.QualityKeeper,
    },
    {
      label: 'Поле для выбора оборудования и порта',
      value: QuestionTypeEnum.Port,
    },
  ];

  constructor(
    private fb: UntypedFormBuilder,
    public activeModal: NgbActiveModal
  ) {}

  get choices(): UntypedFormArray {
    return this.form.get('answer_choices') as UntypedFormArray;
  }

  get answerChoices(): UntypedFormGroup[] {
    return this.choices.controls as UntypedFormGroup[];
  }

  get isChoiceSelected() {
    const questionType = this.form.value.type;

    if (!questionType) {
      return;
    }

    return (
      questionType.value === QuestionTypeEnum.ChoicesMultiple ||
      questionType.value === QuestionTypeEnum.ChoicesMultipleWithComment ||
      questionType.value === QuestionTypeEnum.Choices
    );
  }

  handleChangeType(typeOption: IQuestionTypeOption) {
    if (!typeOption) {
      return;
    }

    this.form.get('text').enable();

    this.choices.clear();

    if (
      typeOption.value === QuestionTypeEnum.Choices ||
      typeOption.value === QuestionTypeEnum.ChoicesMultiple ||
      typeOption.value === QuestionTypeEnum.ChoicesMultipleWithComment
    ) {
      this.choices.push(this.createAnswerChoice());
    }

    if (typeOption.value === QuestionTypeEnum.QualityKeeper) {
      this.form.patchValue({ text: 'quality_keeper' });
      this.form.get('text').disable();
    }
  }

  private createAnswerChoice(): UntypedFormGroup {
    return this.fb.group({
      text: ['', Validators.required],
    });
  }

  toggleAddCategory() {
    this.showCreateCategory = !this.showCreateCategory;
    if (this.showCreateCategory) {
      this.form.get('category').disable();
      this.form.get('createCategory').enable();
    } else {
      this.form.get('category').enable();
      this.form.get('createCategory').disable();
    }
  }

  handleSubmit(): void {
    const { category, type, answer_choices, createCategory } = this.form.value;
    const { text } = this.form.getRawValue();

    this.activeModal.close({
      category: category && category.id,
      createCategory,
      text,
      type: type.value,
      answer_choices,
    });
  }

  handleAddAnswer(): void {
    this.choices.push(this.createAnswerChoice());
  }

  handleRemoveAnswer(index: number): void {
    this.choices.removeAt(index);
  }
}
